/*
 * (C) Manful — gift-bundle engraving editor (mount factory).
 * A self-contained port of the PDP editor's UI (both-sides, inline symbols, live
 * canvas preview, per-product validation) that mounts into an arbitrary container
 * and EXPOSES its data via getData() instead of writing hidden form inputs — so the
 * bundle builder can add it per card without touching the shipped engraving-editor.js.
 *
 * Depends on: window.EngravingRules, window.EngravingPreview (shared pure engines).
 *
 * window.GiftBundleEngraving.mount(container, perCfg, globalCfg, notify) -> api
 *   perCfg   = { engravable, maxChars, maxLines, bothSides }
 *   globalCfg= { fonts:[{label,family}], symbols:[{label,family,char}],
 *               priceOne, priceBoth, oneSideId, bothSidesId }
 *   notify   = fn() called whenever the engraving state changes
 *   api.getData() -> null when engraving is off, else
 *     { on, valid, front, back, font, lines, sideCount, variantId, priceText }
 */
(function () {
  function mount(container, perCfg, globalCfg, notify) {
    if (!window.EngravingRules || !window.EngravingPreview) { return null; }
    function ping() { if (typeof notify === 'function') { notify(); } }

    var FONTS = globalCfg.fonts || [];
    var FONT_LABELS = FONTS.map(function (f) { return f.label; });
    var SYMBOLS = globalCfg.symbols || [];
    var MAX_FRONT = perCfg.maxChars || 20;          // front / single / outside limit
    var MAX_BACK = perCfg.maxCharsBack || MAX_FRONT; // back / inside limit (falls back to front)
    var FRONT_LABEL = perCfg.frontLabel || 'Front';
    var BACK_LABEL = perCfg.backLabel || 'Back';
    var MAXLINES = perCfg.maxLines || 1;
    var MULTI = MAXLINES > 1;
    var CAN_BOTH = !!(perCfg.bothSides && globalCfg.bothSidesId);
    function rulesFor(max) { return { maxChars: max, maxLines: MAXLINES, charsPerLine: max, allowedFonts: FONT_LABELS, allowSymbols: true }; }

    var current = FONTS[0] || { label: '', family: 'inherit' };
    var state = { on: false, font: current };
    var sidesOn = { front: true, back: false };
    var activeSide = null;

    // Single-side pieces: the toggle itself names the engraving surface (e.g. "Signet ring face
    // engraving") so it's obvious without expanding. Multi-side pieces keep "Add engraving" (the
    // front/back picker names the sides).
    var toggleLabel = (!CAN_BOTH && perCfg.location) ? perCfg.location : 'Add engraving';

    var wrap = document.createElement('div');
    wrap.className = 'gbe';
    wrap.innerHTML =
      '<label class="gbe__toggle"><input type="checkbox" class="gbe__cb">' +
        '<span><span class="gbe__toggle-lbl"></span> <span class="gbe__total">' + (globalCfg.priceOne || '') + '</span></span></label>' +
      '<div class="gbe__panel" hidden>' +
        (CAN_BOTH ?
          '<div class="gbe__pick">' +
            '<label class="gbe__pick-opt"><input type="checkbox" class="gbe__pick-front" checked> <span class="gbe__pick-front-lbl"></span> <span class="gbe__price">(+' + (globalCfg.priceOne || '') + ')</span></label>' +
            '<label class="gbe__pick-opt"><input type="checkbox" class="gbe__pick-back"> <span class="gbe__pick-back-lbl"></span> <span class="gbe__price">(+' + (globalCfg.priceOne || '') + ')</span></label>' +
          '</div>' : '') +
        '<div class="gbe__sides"></div>' +
        '<div class="gbe__fonts"></div>' +
        (SYMBOLS.length ? '<div class="gbe__label-row">Tap to add a symbol where your cursor is</div><div class="gbe__symbols"></div>' : '') +
        '<div class="gbe__error" aria-live="polite"></div>' +
      '</div>';
    container.appendChild(wrap);
    wrap.querySelector('.gbe__toggle-lbl').textContent = toggleLabel; // set as text (safe if metafield)
    var pfl = wrap.querySelector('.gbe__pick-front-lbl'); if (pfl) { pfl.textContent = FRONT_LABEL; }
    var pbl = wrap.querySelector('.gbe__pick-back-lbl'); if (pbl) { pbl.textContent = BACK_LABEL; }

    var cb = wrap.querySelector('.gbe__cb');
    var panel = wrap.querySelector('.gbe__panel');
    var sidesBox = wrap.querySelector('.gbe__sides');
    var fontsBox = wrap.querySelector('.gbe__fonts');
    var symbolsBox = wrap.querySelector('.gbe__symbols');
    var totalEl = wrap.querySelector('.gbe__total');
    var pickFront = wrap.querySelector('.gbe__pick-front');
    var pickBack = wrap.querySelector('.gbe__pick-back');
    var errBox = wrap.querySelector('.gbe__error');

    // ---------- one editable side ----------
    function makeSide(name, sideMax) {
      var sw = document.createElement('div');
      sw.className = 'gbe__side';
      sw.innerHTML =
        (CAN_BOTH ? '<div class="gbe__side-label">' + name + '</div>' : '') +
        '<canvas class="gbe__canvas"></canvas>' +
        '<div class="gbe__input" contenteditable="true" role="textbox" aria-multiline="' + (MULTI ? 'true' : 'false') + '" data-ph="Your engraving"></div>' +
        '<div class="gbe__count"></div>';
      var input = sw.querySelector('.gbe__input');
      var canvas = sw.querySelector('.gbe__canvas');
      var count = sw.querySelector('.gbe__count');
      input.style.fontFamily = '"' + state.font.family + '"';

      var savedRange = null;
      function saveRange() { var sel = window.getSelection(); if (sel && sel.rangeCount && input.contains(sel.anchorNode)) { savedRange = sel.getRangeAt(0).cloneRange(); } }
      input.addEventListener('keyup', saveRange);
      input.addEventListener('mouseup', saveRange);
      input.addEventListener('focus', function () { activeSide = api; saveRange(); });
      input.addEventListener('input', refresh);
      // Clicks/keys inside the editor must not bubble to the bundle card (would select it).
      input.addEventListener('click', function (e) { e.stopPropagation(); });
      input.addEventListener('keydown', function (e) {
        e.stopPropagation();
        if (!MULTI && e.key === 'Enter') { e.preventDefault(); }
      });

      function tokens() {
        var out = [];
        function pushText(t) { if (!t) { return; } var last = out[out.length - 1]; if (last && last.type === 'text') { last.value += t; } else { out.push({ type: 'text', value: t }); } }
        (function walk(node) {
          Array.prototype.forEach.call(node.childNodes, function (n) {
            if (n.nodeType === 3) { pushText(n.nodeValue); }
            else if (n.nodeType === 1) {
              if (n.classList && n.classList.contains('gbe__symtok')) { out.push({ type: 'symbol', label: n.getAttribute('data-label'), char: n.getAttribute('data-char'), family: (n.style.fontFamily || '').replace(/["']/g, '') }); }
              else if (n.tagName === 'BR') { pushText('\n'); }
              else { if (/^(DIV|P)$/.test(n.tagName) && out.length) { pushText('\n'); } walk(n); }
            }
          });
        })(input);
        return out;
      }
      function composed() {
        var tk = tokens(), order = '', vtext = '', symbolUsed = false;
        tk.forEach(function (t) {
          if (t.type === 'symbol') { order += '{' + t.label + '}'; vtext += '*'; symbolUsed = true; }
          else { order += t.value; vtext += t.value; }
        });
        return { tokens: tk, order: order, vtext: vtext, visibleLen: vtext.replace(/\n/g, '').length, lines: vtext.length ? vtext.split('\n').length : 0, symbolUsed: symbolUsed };
      }
      function setCanvasSize() {
        var dpr = window.devicePixelRatio || 1;
        var w = canvas.clientWidth || 280, h = canvas.clientHeight || 84;
        canvas.width = w * dpr; canvas.height = h * dpr;
        canvas.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
        return { w: w, h: h };
      }
      function draw() {
        var dims = setCanvasSize();
        window.EngravingPreview.renderInline(canvas, composed().tokens, { width: dims.w, height: dims.h, fontSize: 34, textFont: state.font.family, color: '#1a1a1a' });
      }
      function loadAndRender() {
        if (document.fonts && document.fonts.load) {
          var fams = {}; fams[state.font.family] = 1;
          composed().tokens.forEach(function (t) { if (t.type === 'symbol' && t.family) { fams[t.family] = 1; } });
          Promise.all(Object.keys(fams).map(function (f) { return document.fonts.load('34px "' + f + '"', 'Ag').catch(function () {}); })).then(draw, draw);
        } else { draw(); }
      }
      function insertSymbol(s) {
        var span = document.createElement('span');
        span.className = 'gbe__symtok'; span.setAttribute('contenteditable', 'false');
        span.setAttribute('data-label', s.label); span.setAttribute('data-char', s.char);
        span.style.fontFamily = '"' + s.family + '"'; span.textContent = s.char;
        if (!savedRange || !input.contains(savedRange.startContainer)) { input.appendChild(span); }
        else { savedRange.deleteContents(); savedRange.insertNode(span); }
        var sel = window.getSelection(); var after = document.createRange(); after.setStartAfter(span); after.collapse(true);
        sel.removeAllRanges(); sel.addRange(after); saveRange(); input.focus(); refresh();
      }
      function restyle() { input.style.fontFamily = '"' + state.font.family + '"'; }
      var api = { wrap: sw, input: input, count: count, composed: composed, draw: draw, loadAndRender: loadAndRender, insertSymbol: insertSymbol, restyle: restyle, max: sideMax, label: name };
      return api;
    }

    var front = makeSide(FRONT_LABEL, MAX_FRONT); sidesBox.appendChild(front.wrap);
    var back = CAN_BOTH ? makeSide(BACK_LABEL, MAX_BACK) : null;
    if (back) { sidesBox.appendChild(back.wrap); }
    if (CAN_BOTH) { sidesBox.classList.add('is-both'); }

    // ---------- font chips (apply to every side) ----------
    FONTS.forEach(function (f, i) {
      var chip = document.createElement('button');
      chip.type = 'button'; chip.className = 'gbe__font'; chip.textContent = f.label;
      chip.style.fontFamily = '"' + f.family + '"'; chip.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
      chip.addEventListener('click', function (e) {
        e.stopPropagation();
        state.font = f;
        Array.prototype.forEach.call(fontsBox.children, function (c) { c.setAttribute('aria-pressed', 'false'); });
        chip.setAttribute('aria-pressed', 'true');
        front.restyle(); if (back) { back.restyle(); }
        refresh();
      });
      fontsBox.appendChild(chip);
    });

    // ---------- symbol chips (insert into the active side) ----------
    if (symbolsBox) {
      SYMBOLS.forEach(function (s) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'gbe__sym'; b.textContent = s.char;
        b.style.fontFamily = '"' + s.family + '"'; b.title = s.label; b.setAttribute('aria-label', 'Add ' + s.label);
        b.addEventListener('mousedown', function (e) { e.preventDefault(); });
        b.addEventListener('click', function (e) { e.stopPropagation(); (activeSide && activeSide.wrap.hidden === false ? activeSide : front).insertSymbol(s); });
        symbolsBox.appendChild(b);
      });
    }

    function sideCount() { return (sidesOn.front ? 1 : 0) + (sidesOn.back && back ? 1 : 0); }
    function selected() { var a = []; if (sidesOn.front) { a.push(front); } if (sidesOn.back && back) { a.push(back); } return a; }
    function applyVisibility() { front.wrap.hidden = !sidesOn.front; if (back) { back.wrap.hidden = !sidesOn.back; } }
    function updateTotal() { if (totalEl) { totalEl.textContent = sideCount() >= 2 ? (globalCfg.priceBoth || globalCfg.priceOne || '') : (globalCfg.priceOne || ''); } }

    function validateNow() {
      var sel = selected();
      if (sel.length === 0) { errBox.textContent = 'Choose ' + FRONT_LABEL.toLowerCase() + ' or ' + BACK_LABEL.toLowerCase() + ' to engrave.'; return false; }
      var allValid = true, firstErr = '';
      sel.forEach(function (s) {
        var c = s.composed();
        var r = window.EngravingRules.validate({ text: c.vtext, font: state.font.label, symbol: c.symbolUsed ? {} : null, lines: c.lines }, rulesFor(s.max));
        s.count.textContent = c.visibleLen + ' / ' + s.max;
        if (!r.valid) { allValid = false; if (!firstErr) { firstErr = (CAN_BOTH ? (s.label + ': ') : '') + r.errors[0]; } }
      });
      errBox.textContent = allValid ? '' : firstErr;
      return allValid;
    }

    // Internal UI driver — re-lays out, validates, then pings the host (card).
    function refresh() {
      applyVisibility(); updateTotal();
      if (state.on) { selected().forEach(function (s) { s.loadAndRender(); }); }
      validateNow();
      ping();
    }

    function setOn(on) {
      state.on = on; panel.hidden = !on;
      applyVisibility(); updateTotal();
      if (on) { selected().forEach(function (s) { s.loadAndRender(); }); (selected()[0] || front).input.focus(); }
      validateNow();
      ping();
    }

    cb.addEventListener('change', function () { setOn(this.checked); });
    if (pickFront) { pickFront.addEventListener('change', function () { sidesOn.front = this.checked; refresh(); }); }
    if (pickBack) { pickBack.addEventListener('change', function () { sidesOn.back = this.checked; refresh(); }); }
    // The whole editor swallows card-select clicks.
    wrap.addEventListener('click', function (e) { e.stopPropagation(); });

    applyVisibility();
    front.count.textContent = '0 / ' + MAX_FRONT;

    function getData() {
      if (!state.on) { return null; }
      var valid = validateNow() && sideCount() > 0;
      var both = sideCount() >= 2;
      return {
        on: true,
        valid: valid,
        front: sidesOn.front ? front.composed().order : '',
        back: (sidesOn.back && back) ? back.composed().order : '',
        font: state.font.label,
        lines: String(sidesOn.front ? front.composed().lines : (back ? back.composed().lines : 0)),
        sideCount: sideCount(),
        variantId: both ? (globalCfg.bothSidesId || globalCfg.oneSideId) : globalCfg.oneSideId,
        priceText: both ? (globalCfg.priceBoth || globalCfg.priceOne) : globalCfg.priceOne
      };
    }

    return { getData: getData, el: wrap };
  }

  window.GiftBundleEngraving = { mount: mount };
})();
